import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  ArrowUpDown,
  DollarSign,
  HeartPulse,
  Loader2,
  Mail,
  Search,
  Users,
} from 'lucide-react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { scraperQuotaApi } from '../../../api/scraperQuotaApi';
import { adminApiClient } from '../../../utils/apiClient';

interface ScraperClientUsage {
  client_id: number;
  client_name: string;
  client_email: string;
  scraper_enabled: boolean;
  auto_used_today: number;
  auto_run_per_day: number;
  extra_used_period: number;
  extra_runs_limit: number;
  total_estimated_cost: number;
  total_url_count: number;
  total_article_count: number;
  recent_runs: number;
}

interface DashboardState {
  totalSubscribers: number;
  totalModerators: number;
  openAISpend: number;
  openAIBudget: number;
  activeScraperSources: number;
  scraperClients: ScraperClientUsage[];
  loading: boolean;
  error: string | null;
}

const glassKpi =
  'relative flex h-[8.5rem] min-w-0 flex-col gap-0 overflow-hidden rounded-[1.25rem] border border-sky-100/90 !bg-[linear-gradient(160deg,rgba(255,255,255,0.82)_0%,rgba(239,246,255,0.58)_100%)] shadow-[0_6px_18px_rgba(14,165,233,0.07),0_1px_0_rgba(255,255,255,0.8)_inset] backdrop-blur-xl backdrop-saturate-150 sm:h-[9rem]';

const glassPanel =
  'flex min-h-0 min-w-0 shrink-0 flex-col gap-0 overflow-hidden rounded-2xl border border-white/50 !bg-white/40 shadow-[0_8px_32px_rgba(15,23,42,0.08)] backdrop-blur-xl backdrop-saturate-150 ring-1 ring-indigo-200/35';

const fieldClass =
  'h-9 w-full rounded-xl border border-white/60 bg-white/90 px-3 text-sm text-[#0f172a] outline-none transition-[box-shadow,border-color] placeholder:text-slate-400 focus:border-[#1d4ed8]/40 focus:ring-[3px] focus:ring-[#1d4ed8]/12';

const fontStack = 'font-[Inter,ui-sans-serif,system-ui,sans-serif]';

function useCountUp(target: number, durationMs = 900) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const safeTarget = Number.isFinite(target) ? target : 0;
    let rafId = 0;
    const start = performance.now();
    const initial = value;
    const delta = safeTarget - initial;

    if (delta === 0) {
      return;
    }

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs);
      const eased = 1 - (1 - progress) ** 3;
      setValue(initial + delta * eased);
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
    // Re-animate only when target changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return value;
}

type UsageSortKey = 'name' | 'auto' | 'extra' | 'articles' | 'urls' | 'cost';
type UsageStatusFilter = 'all' | 'enabled' | 'disabled';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function quotaTone(used: number, limit: number): string {
  if (limit <= 0) return 'text-slate-500';
  const ratio = used / limit;
  if (ratio >= 1) return 'text-red-600 font-semibold';
  if (ratio >= 0.8) return 'text-amber-600 font-medium';
  return 'text-slate-600';
}

function budgetBarClass(percent: number): string {
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 70) return 'bg-amber-500';
  return 'bg-gradient-to-r from-[#0284c7] to-[#1d4ed8]';
}

function SortHeader({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors ${
        active ? 'text-[#1e3a8a]' : 'text-slate-500 hover:text-[#0f172a]'
      }`}
      onClick={onClick}
    >
      {label}
      <ArrowUpDown className={`h-3 w-3 ${active ? 'text-[#1d4ed8]' : 'text-slate-400'}`} />
    </button>
  );
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const [state, setState] = useState<DashboardState>({
    totalSubscribers: 0,
    totalModerators: 0,
    openAISpend: 0,
    openAIBudget: 2000,
    activeScraperSources: 0,
    scraperClients: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    const controller = new AbortController();
    const today = new Date().toISOString().slice(0, 10);

    Promise.allSettled([
      adminApiClient.get('/admin/openai-usage-stats/summary', {
        params: { target_date: today },
        signal: controller.signal,
      }),
      adminApiClient.get('/admin/stats/overview', { signal: controller.signal }),
      scraperQuotaApi.usageOverview({ signal: controller.signal }),
    ]).then(([usageRes, statsRes, quotaRes]) => {
      let spend = 0;
      if (usageRes.status === 'fulfilled') {
        const u = usageRes.value.data as any;
        spend = Number(u?.total_price ?? u?.total_cost ?? u?.cost ?? 0);
      }

      let totalSubscribers = 0;
      let totalModerators = 0;
      let activeScraperSources = 0;
      if (statsRes.status === 'fulfilled') {
        const s = statsRes.value.data as any;
        totalSubscribers = Number(s?.total_subscribers ?? s?.subscriber_count ?? 0);
        totalModerators = Number(s?.total_moderators ?? s?.moderator_count ?? 0);
        activeScraperSources = Number(s?.active_scraper_sources ?? 0);
      }

      let scraperClients: ScraperClientUsage[] = [];
      if (quotaRes.status === 'fulfilled') {
        const payload = quotaRes.value.data as any;
        scraperClients = Array.isArray(payload?.clients) ? payload.clients : [];
      }

      setState((prev) => ({
        ...prev,
        openAISpend: spend,
        totalSubscribers,
        totalModerators,
        activeScraperSources,
        scraperClients,
        loading: false,
      }));
    });

    return () => controller.abort();
  }, []);

  const {
    totalSubscribers,
    totalModerators,
    openAISpend,
    openAIBudget,
    activeScraperSources,
    scraperClients,
    loading,
  } = state;
  const animatedSpend = useCountUp(openAISpend);
  const animatedSubscribers = useCountUp(totalSubscribers);
  const animatedModerators = useCountUp(totalModerators);
  const animatedSources = useCountUp(activeScraperSources);
  const usagePercent = openAIBudget > 0 ? Math.min(100, (openAISpend / openAIBudget) * 100) : 0;
  const scraperCostTotal = scraperClients.reduce(
    (sum, c) => sum + Number(c.total_estimated_cost || 0),
    0,
  );
  const [usageQuery, setUsageQuery] = useState('');
  const [usageStatus, setUsageStatus] = useState<UsageStatusFilter>('all');
  const [usageSortKey, setUsageSortKey] = useState<UsageSortKey>('cost');
  const [usageSortDir, setUsageSortDir] = useState<'asc' | 'desc'>('desc');

  const usageRows = useMemo(() => {
    const query = usageQuery.trim().toLowerCase();
    const filtered = scraperClients.filter((client) => {
      if (usageStatus === 'enabled' && !client.scraper_enabled) return false;
      if (usageStatus === 'disabled' && client.scraper_enabled) return false;
      if (!query) return true;
      return (
        client.client_name.toLowerCase().includes(query)
        || client.client_email.toLowerCase().includes(query)
      );
    });

    const sorted = [...filtered].sort((a, b) => {
      const direction = usageSortDir === 'asc' ? 1 : -1;
      switch (usageSortKey) {
        case 'name':
          return a.client_name.localeCompare(b.client_name) * direction;
        case 'auto':
          return (
            (a.auto_used_today / Math.max(1, a.auto_run_per_day)
              - b.auto_used_today / Math.max(1, b.auto_run_per_day))
            * direction
          );
        case 'extra':
          return (
            (a.extra_used_period / Math.max(1, a.extra_runs_limit)
              - b.extra_used_period / Math.max(1, b.extra_runs_limit))
            * direction
          );
        case 'articles':
          return (a.total_article_count - b.total_article_count) * direction;
        case 'urls':
          return (a.total_url_count - b.total_url_count) * direction;
        case 'cost':
        default:
          return (
            (Number(a.total_estimated_cost || 0) - Number(b.total_estimated_cost || 0)) * direction
          );
      }
    });

    return sorted;
  }, [scraperClients, usageQuery, usageStatus, usageSortKey, usageSortDir]);

  const toggleSort = (key: UsageSortKey) => {
    setUsageSortKey((prev) => {
      if (prev === key) {
        setUsageSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setUsageSortDir('desc');
      return key;
    });
  };

  return (
    <div
      className={`hawk-admin-surface flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${fontStack}`}
    >
      <header className="shrink-0">
        <div className="mx-auto w-full max-w-[1580px] px-3 py-3 sm:px-4 sm:py-3.5 lg:px-6">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/50 bg-white/40 text-[#1d4ed8] shadow-[0_8px_24px_rgba(15,23,42,0.06)] backdrop-blur-xl ring-1 ring-indigo-200/30">
              <HeartPulse className="h-4 w-4" strokeWidth={2.1} />
            </span>
            <div className="min-w-0">
              <h1 className="text-[1.35rem] font-bold tracking-tight text-[#0f172a] sm:text-[1.55rem] lg:text-[1.7rem]">
                System Health
              </h1>
              <p className="mt-0.5 text-[11px] leading-tight text-slate-500 sm:text-xs">
                Platform at-a-glance overview
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="scrollbar-hide mx-auto flex min-h-0 min-w-0 w-full max-w-[1580px] flex-1 touch-pan-y flex-col gap-3 overflow-x-hidden overflow-y-auto px-2.5 pb-3 sm:gap-3.5 sm:px-4 sm:pb-4 lg:px-6 lg:pb-5 [-webkit-overflow-scrolling:touch]">
        {/* KPI row — pearl glass */}
        <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:gap-3.5">
          {/* OpenAI Spend */}
          <Card className={glassKpi}>
            <DollarSign
              className="pointer-events-none absolute -bottom-1 -right-1 h-20 w-20 text-slate-300/25 sm:h-24 sm:w-24"
              strokeWidth={1}
              aria-hidden
            />
            <CardHeader className="relative z-[1] space-y-0 px-4 pb-0 pt-3.5 sm:px-4 sm:pt-4">
              <CardTitle className="text-[13px] font-medium leading-snug text-slate-500 sm:text-sm">
                OpenAI Spend (Today)
              </CardTitle>
            </CardHeader>
            <CardContent className="relative z-[1] flex flex-1 flex-col justify-end px-4 pb-3.5 pt-2 sm:px-4 sm:pb-4">
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin text-[#0284c7]" />
              ) : (
                <div className="space-y-1.5">
                  <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0">
                    <span className="text-[1.875rem] font-bold leading-none tabular-nums tracking-tight text-[#0f172a] sm:text-[2rem]">
                      $
                      {animatedSpend.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                    <span className="text-[15px] font-medium tabular-nums text-slate-500 sm:text-[16px]">
                      / ${openAIBudget}
                    </span>
                  </div>
                  <div className="h-1 w-full max-w-[10.5rem] rounded-full bg-sky-100/80">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${usagePercent}%` }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className={`h-1 rounded-full ${budgetBarClass(usagePercent)}`}
                    />
                  </div>
                  <p
                    className={`text-[11px] leading-tight ${
                      usagePercent >= 90
                        ? 'font-medium text-red-600'
                        : usagePercent >= 70
                          ? 'font-medium text-amber-600'
                          : 'text-slate-500'
                    }`}
                  >
                    {usagePercent.toFixed(1)}% of monthly budget
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Total Subscribers */}
          <Card className={glassKpi}>
            <Users
              className="pointer-events-none absolute -bottom-1 -right-1 h-20 w-20 text-slate-300/25 sm:h-24 sm:w-24"
              strokeWidth={1}
              aria-hidden
            />
            <CardHeader className="relative z-[1] space-y-0 px-4 pb-0 pt-3.5 sm:px-4 sm:pt-4">
              <CardTitle className="text-[13px] font-medium leading-snug text-slate-500 sm:text-sm">
                Total Subscribers
              </CardTitle>
            </CardHeader>
            <CardContent className="relative z-[1] flex flex-1 flex-col justify-end px-4 pb-3.5 pt-2 sm:px-4 sm:pb-4">
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin text-[#0284c7]" />
              ) : (
                <div className="space-y-1.5">
                  <p className="text-[1.875rem] font-bold leading-none tabular-nums tracking-tight text-[#0f172a] sm:text-[2rem]">
                    {Math.round(animatedSubscribers).toLocaleString()}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#059669]" />
                    <span className="text-[12px] font-medium text-[#059669]">Live data</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Total Moderators */}
          <Card className={glassKpi}>
            <Activity
              className="pointer-events-none absolute -bottom-1 -right-1 h-20 w-20 text-slate-300/25 sm:h-24 sm:w-24"
              strokeWidth={1}
              aria-hidden
            />
            <CardHeader className="relative z-[1] space-y-0 px-4 pb-0 pt-3.5 sm:px-4 sm:pt-4">
              <CardTitle className="text-[13px] font-medium leading-snug text-slate-500 sm:text-sm">
                Total Moderators
              </CardTitle>
            </CardHeader>
            <CardContent className="relative z-[1] flex flex-1 flex-col justify-end px-4 pb-3.5 pt-2 sm:px-4 sm:pb-4">
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin text-[#0284c7]" />
              ) : (
                <p className="text-[1.875rem] font-bold leading-none tabular-nums tracking-tight text-[#0f172a] sm:text-[2rem]">
                  {Math.round(animatedModerators).toLocaleString()}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Active Scraper Sources */}
          <Card className={glassKpi}>
            <Mail
              className="pointer-events-none absolute -bottom-1 -right-1 h-20 w-20 text-slate-300/25 sm:h-24 sm:w-24"
              strokeWidth={1}
              aria-hidden
            />
            <CardHeader className="relative z-[1] space-y-0 px-4 pb-0 pt-3.5 sm:px-4 sm:pt-4">
              <CardTitle className="text-[13px] font-medium leading-snug text-slate-500 sm:text-sm">
                Active Scraper Sources
              </CardTitle>
            </CardHeader>
            <CardContent className="relative z-[1] flex flex-1 flex-col justify-end px-4 pb-3.5 pt-2 sm:px-4 sm:pb-4">
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin text-[#0284c7]" />
              ) : (
                <p className="text-[1.875rem] font-bold leading-none tabular-nums tracking-tight text-[#1e3a8a] sm:text-[2rem]">
                  {Math.round(animatedSources).toLocaleString()}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Scraper usage table */}
        <Card className={glassPanel}>
          <CardHeader className="border-b border-white/40 px-3.5 py-3 sm:px-5 sm:py-3.5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <CardTitle className="text-[15px] font-bold tracking-tight text-[#0f172a] sm:text-base">
                  Scraper usage &amp; estimated cost
                </CardTitle>
                <p className="mt-0.5 text-[11px] leading-snug text-slate-500 sm:text-xs">
                  Monitor moderator activity, content scraping volume, and estimated costs.
                </p>
              </div>
              <span className="inline-flex shrink-0 items-center self-start rounded-full border border-white/60 bg-white/50 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-[#0284c7] backdrop-blur-sm ring-1 ring-sky-200/40 sm:text-xs">
                Total est. ${scraperCostTotal.toFixed(2)}
              </span>
            </div>
          </CardHeader>

          <CardContent className="flex min-h-0 min-w-0 flex-col overflow-hidden px-3.5 py-3 sm:px-5 sm:py-3.5">
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-[#0284c7]" />
            ) : !scraperClients.length ? (
              <p className="py-8 text-center text-sm text-slate-400">No client scraper usage yet.</p>
            ) : (
              <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
                <div className="flex min-w-0 shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="relative w-full sm:max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <input
                      value={usageQuery}
                      onChange={(e) => setUsageQuery(e.target.value)}
                      placeholder="Search moderator or email..."
                      className={`${fieldClass} pl-9`}
                      aria-label="Search moderator or email"
                    />
                  </div>
                  <div className="flex w-full items-center gap-2 sm:w-auto">
                    <label className="shrink-0 text-[11px] font-medium text-slate-500 sm:text-xs">
                      Status
                    </label>
                    <select
                      value={usageStatus}
                      onChange={(e) => setUsageStatus(e.target.value as UsageStatusFilter)}
                      className={`${fieldClass} sm:w-[8.5rem]`}
                    >
                      <option value="all">All</option>
                      <option value="enabled">ON</option>
                      <option value="disabled">Locked</option>
                    </select>
                  </div>
                </div>

                <div className="min-h-0 min-w-0 flex-1 overflow-hidden rounded-xl border border-white/50 bg-white/50 backdrop-blur-sm">
                  <div className="scrollbar-hide min-h-0 min-w-0 overflow-x-auto overflow-y-auto overscroll-contain touch-pan-y lg:max-h-[34rem] [-webkit-overflow-scrolling:touch]">
                    <table className="w-full min-w-[780px] text-sm sm:min-w-[880px]">
                      <thead className="sticky top-0 z-10 border-b border-white/40 bg-white/55 backdrop-blur-md">
                        <tr>
                          <th className="whitespace-nowrap px-3.5 py-2.5 text-left sm:px-4">
                            <SortHeader
                              label="Moderator"
                              active={usageSortKey === 'name'}
                              onClick={() => toggleSort('name')}
                            />
                          </th>
                          <th className="whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">
                            Status
                          </th>
                          <th className="whitespace-nowrap px-3 py-2.5 text-left">
                            <SortHeader
                              label="Auto"
                              active={usageSortKey === 'auto'}
                              onClick={() => toggleSort('auto')}
                            />
                          </th>
                          <th className="whitespace-nowrap px-3 py-2.5 text-left">
                            <SortHeader
                              label="Extra"
                              active={usageSortKey === 'extra'}
                              onClick={() => toggleSort('extra')}
                            />
                          </th>
                          <th className="whitespace-nowrap px-3 py-2.5 text-left">
                            <SortHeader
                              label="Articles"
                              active={usageSortKey === 'articles'}
                              onClick={() => toggleSort('articles')}
                            />
                          </th>
                          <th className="whitespace-nowrap px-3 py-2.5 text-left">
                            <SortHeader
                              label="URLs"
                              active={usageSortKey === 'urls'}
                              onClick={() => toggleSort('urls')}
                            />
                          </th>
                          <th className="whitespace-nowrap px-3.5 py-2.5 text-left sm:px-4">
                            <SortHeader
                              label="Est. Cost"
                              active={usageSortKey === 'cost'}
                              onClick={() => toggleSort('cost')}
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/40 bg-transparent">
                        {usageRows.map((c) => (
                          <tr
                            key={c.client_id}
                            role="link"
                            tabIndex={0}
                            aria-label={`Open settings for ${c.client_name}`}
                            onClick={() =>
                              navigate(`/settings?clientId=${c.client_id}`)
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate(`/settings?clientId=${c.client_id}`);
                              }
                            }}
                            className="cursor-pointer transition-colors hover:bg-white/40 focus-visible:bg-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1d4ed8]/25"
                          >
                            <td className="whitespace-nowrap px-3.5 py-3 sm:px-4">
                              <div className="flex items-center gap-2.5">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0284c7] to-[#1d4ed8] text-[11px] font-semibold text-white shadow-sm">
                                  {initials(c.client_name)}
                                </span>
                                <div className="min-w-0">
                                  <p className="truncate text-[13px] font-semibold text-[#0f172a]">
                                    {c.client_name}
                                  </p>
                                  <p className="truncate text-[11px] text-slate-500">
                                    {c.client_email}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-3">
                              <span
                                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
                                  c.scraper_enabled
                                    ? 'bg-emerald-50 text-[#059669] ring-1 ring-inset ring-emerald-200/80'
                                    : 'bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200'
                                }`}
                              >
                                <span
                                  className={`h-1.5 w-1.5 rounded-full ${
                                    c.scraper_enabled ? 'bg-[#059669]' : 'bg-slate-400'
                                  }`}
                                />
                                {c.scraper_enabled ? 'ON' : 'Locked'}
                              </span>
                            </td>
                            <td
                              className={`whitespace-nowrap px-3 py-3 text-[13px] tabular-nums ${quotaTone(
                                c.auto_used_today,
                                c.auto_run_per_day,
                              )}`}
                            >
                              {c.auto_used_today}/{c.auto_run_per_day}
                            </td>
                            <td
                              className={`whitespace-nowrap px-3 py-3 text-[13px] tabular-nums ${quotaTone(
                                c.extra_used_period,
                                c.extra_runs_limit,
                              )}`}
                            >
                              {c.extra_used_period}/{c.extra_runs_limit}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-[13px] font-medium tabular-nums text-[#1d4ed8]">
                              {c.total_article_count.toLocaleString()}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-[13px] font-medium tabular-nums text-[#1d4ed8]">
                              {c.total_url_count.toLocaleString()}
                            </td>
                            <td className="whitespace-nowrap px-3.5 py-3 text-[13px] font-bold tabular-nums text-[#1e3a8a] sm:px-4">
                              ${Number(c.total_estimated_cost || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
